import PlanningApplicationPage, {
  getStaticProps as getPlanningApplicationStaticProps,
} from "../planning-application/[...reference]";
import PlanningUpdates, {
  getPlanningUpdatesStaticProps,
} from "../planning-updates";
import { getCouncilConfig } from "../../lib/councils";
import { resolvePlanningAreaRoute } from "../../lib/planningAreaRoutes";

function normalizeReferenceSegments(value) {
  if (Array.isArray(value)) {
    return value.flatMap((segment) => String(segment).split("/")).filter(Boolean);
  }
  if (typeof value !== "string" || !value.trim()) return [];
  return value.split("/").filter(Boolean);
}

function appendCleanQuery(destination, query = {}) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query || {})) {
    if (key === "referenceSegments" || key === "council") continue;
    const values = Array.isArray(value) ? value : [value];
    for (const item of values) {
      if (item === undefined || item === null || item === "") continue;
      params.append(key, String(item));
    }
  }
  const queryString = params.toString();
  return queryString ? `${destination}?${queryString}` : destination;
}

export function isCompletePlanningReferenceSegments(segments, council = null) {
  if (!Array.isArray(segments)) return false;
  const reference = segments.join("/");
  // London sources issue compact, two-part and series-first references. Resolve
  // the exact council-qualified identity through the source reader; an absent
  // identity returns 404 rather than being reinterpreted as a Scottish area.
  if (council?.region === 'london') {
    return reference.length <= 120 && /^[A-Za-z0-9][A-Za-z0-9/_.()-]*$/.test(reference);
  }
  // These councils issue series-first or compact references rather than
  // year/number/type. Keep recognition scoped to their documented formats.
  if (council?.slug === "west-dunbartonshire") {
    return /^DC\d{2}\/\d{3,6}\/[A-Za-z]{1,12}$/i.test(reference)
      || /^(?:ARCD|HH|PRIOR|TPO)\d{2}\/\d{3,6}$/i.test(reference);
  }
  if (council?.slug === "aberdeen-city" && /^\d{6}\/[A-Za-z][A-Za-z0-9]{0,11}$/i.test(reference)) return true;
  if (council?.slug === "aberdeenshire" && /^(?:APP|ENQ|TRE)\/\d{4}\/\d{3,6}$/i.test(reference)) return true;
  if (council?.slug === "east-dunbartonshire" && /^TP\/ED\/\d{2}\/\d{3,6}$/i.test(reference)) return true;
  if (council?.slug === "south-lanarkshire" && (
    /^P\/\d{2}\/\d{3,6}(?:\/(?:PAN|V))?$/i.test(reference)
    || /^(?:CAT|CGA)\/\d{2}\/\d{3,6}$/i.test(reference)
  )) return true;
  // Dundee condition applications use CND/number/year alongside its
  // ordinary year/number/type references.
  if (council?.slug === "dundee" && /^CND\/\d{4}\/\d{4}$/i.test(reference)) return true;
  if (council?.slug === "perth-kinross" && /^\d{2}\/\d{5}\/42[LM]$/i.test(reference)) return true;
  if (council?.slug === "falkirk" && /^P\/\d{2}\/\d{4}\/75M$/i.test(reference)) return true;
  if (council?.slug === "clackmannanshire" && /^EIA-\d{4}-\d{3}$/i.test(reference)) return true;
  if (council?.slug === "stirling" && (
    /^(?:PAN|TREENQ)-\d{4}-\d{3}$/i.test(reference)
    || /^SCRN-\d{2}-\d{3}$/i.test(reference)
    || /^S36-\d{2}-\d{2}$/i.test(reference)
  )) return true;
  if (segments.length < 3) return false;
  const [year, number, type] = segments;
  // Shetland retains four-digit years, including condition applications on
  // older cases such as 2023/084/DCON18 received in the current coverage year.
  const usesYearFirstFormat = /^(?:\d{2}|\d{4})$/.test(year || "")
    && /^\d{3,6}[A-Za-z0-9_.-]*$/.test(number || "")
    && /^[A-Za-z]{1,12}[A-Za-z0-9_.-]*$/.test(type || "");
  if (usesYearFirstFormat) return true;

  // Falkirk references include a leading application-series segment, for
  // example P/26/0334/FUL. Treat the full four-part value as an application
  // reference so the canonical detail route can return real content or a
  // real 404; redirecting it to the council feed creates a soft 404.
  if (council?.slug === "falkirk" && segments.length >= 4) {
    const [series, falkirkYear, falkirkNumber, falkirkType] = segments;
    return /^[A-Za-z]{1,3}$/.test(series || "")
      && /^\d{2}$/.test(falkirkYear || "")
      && /^\d{3,6}[A-Za-z0-9_.-]*$/.test(falkirkNumber || "")
      && /^[A-Za-z]{1,12}[A-Za-z0-9_.-]*$/.test(falkirkType || "");
  }

  // West Lothian references are issued as number/type/year (for example
  // 0606/H/26), while the other public Idox councils use year/number/type.
  return council?.slug === "west-lothian"
    && /^\d{3,6}[A-Za-z0-9_.-]*$/.test(year || "")
    && /^[A-Za-z]{1,12}[A-Za-z0-9_.-]*$/.test(number || "")
    && /^\d{2}$/.test(type || "");
}

function isPlanningAreaSlug(value) {
  return /^[a-z0-9][a-z0-9-]{1,80}$/i.test(String(value || ""));
}

function hasPlaceholderReferenceSegment(segments) {
  return segments.some((segment) => /^(?:null|undefined|nan)$/i.test(String(segment || "").trim()));
}

export async function getStaticPaths() {
  return { paths: [], fallback: "blocking" };
}

export async function getStaticProps(context) {
  const segments = normalizeReferenceSegments(context.params?.referenceSegments);
  const firstSegment = segments[0] || "";
  const lowercaseSegment = firstSegment.toLowerCase();
  const council = getCouncilConfig(lowercaseSegment);

  if (council && !council.publicEnabled) {
    return { notFound: true };
  }

  const areaSlug = council?.region !== 'london' && segments.length === 2 && isPlanningAreaSlug(segments[1])
    && !isCompletePlanningReferenceSegments([segments[1]], council)
    ? segments[1]
    : null;
  let area = null;
  if (council && areaSlug) {
    try {
      area = await resolvePlanningAreaRoute(council.slug, areaSlug);
    } catch (error) {
      console.warn("Planning updates area lookup unavailable", {
        council: council.slug,
        areaSlug,
        error: error instanceof Error ? error.message : String(error),
      });
      return {
        redirect: {
          destination: appendCleanQuery(
            `/planning-updates/${council.shortCode}`,
            {},
          ),
          permanent: false,
        },
      };
    }
  }

  if (areaSlug && !area) {
    return { notFound: true };
  }

  if (council && firstSegment !== council.shortCode) {
    const canonicalSegments = [council.shortCode, ...segments.slice(1)];
    return {
      redirect: {
        destination: appendCleanQuery(
          `/planning-updates/${canonicalSegments.map(encodeURIComponent).join("/")}`,
          {},
        ),
        permanent: true,
      },
    };
  }

  if (council) {
    const reference = segments.slice(1);
    if (reference.length === 0) {
      return {
        redirect: {
          destination: `/planning-updates/${council.shortCode}`,
          permanent: true,
        },
      };
    }

    if (hasPlaceholderReferenceSegment(reference)) {
      return { notFound: true };
    }

    if (areaSlug) {
      const areaPropsResult = await getPlanningUpdatesStaticProps({
        includeApplications: true,
        councilSlug: council.slug,
        areaId: area.id,
      });
      return areaPropsResult;
    }

    if (!isCompletePlanningReferenceSegments(reference, council)) {
      return {
        redirect: {
          destination: appendCleanQuery(
            `/planning-updates/${council.shortCode}`,
            {
              ref: reference.join("/"),
            },
          ),
          permanent: true,
        },
      };
    }

    return getPlanningApplicationStaticProps({
      ...context,
      params: {
        reference: [council.shortCode, ...reference],
      },
    });
  }

  if (/^[a-z]{3,4}$/i.test(firstSegment)) {
    return { notFound: true };
  }

  return getPlanningApplicationStaticProps({
    ...context,
    params: {
      reference: segments,
    },
  });
}

function PlanningUpdatesCatchAllPage(props) {
  if (props?.areaId) {
    return <PlanningUpdates {...props} />;
  }
  return <PlanningApplicationPage {...props} />;
}

export default PlanningUpdatesCatchAllPage;
